<template>
  <div
    class="ur-row"
    :class="{ 'ur-row--dragging': isDragging }"
    draggable="true"
    @dragstart.stop="$emit('drag-start', unit.id)"
    @dragover.prevent.stop="$emit('drag-over', index)"
    @drop.stop="$emit('drop')"
    @dragend.stop="$emit('drag-end')"
  >
    <!-- Drag handle -->
    <div
      class="ur-handle"
      aria-label="Arrastrar para reordenar"
      role="button"
      tabindex="0"
      @keydown.space.prevent="() => {}"
    >
      <q-icon name="drag_indicator" size="16px" />
      <q-tooltip>Arrastrar para reordenar</q-tooltip>
    </div>

    <!-- Unit number badge -->
    <div class="ur-badge">{{ unitNumber }}</div>

    <!-- Title -->
    <span class="ur-title">{{ unit.title }}</span>

    <!-- Reorder arrows (keyboard-accessible alternative) -->
    <q-btn
      flat
      dense
      round
      icon="arrow_upward"
      size="xs"
      class="ur-action ur-action--arrow"
      aria-label="Mover unidad arriba"
      :disable="isFirst"
      @click.stop="$emit('move', index, index - 1)"
    >
      <q-tooltip>Mover arriba</q-tooltip>
    </q-btn>

    <q-btn
      flat
      dense
      round
      icon="arrow_downward"
      size="xs"
      class="ur-action ur-action--arrow"
      aria-label="Mover unidad abajo"
      :disable="isLast"
      @click.stop="$emit('move', index, index + 1)"
    >
      <q-tooltip>Mover abajo</q-tooltip>
    </q-btn>

    <!-- Edit / Delete (hover-revealed on desktop) -->
    <q-btn
      flat
      dense
      round
      icon="edit"
      size="xs"
      class="ur-action"
      aria-label="Editar unidad"
      @click.stop="$emit('edit', unit)"
    >
      <q-tooltip>Editar unidad</q-tooltip>
    </q-btn>

    <q-btn
      flat
      dense
      round
      icon="delete"
      size="xs"
      class="ur-action ur-action--danger"
      aria-label="Eliminar unidad"
      @click.stop="$emit('delete', unit)"
    >
      <q-tooltip>Eliminar unidad</q-tooltip>
    </q-btn>
  </div>
</template>

<script setup lang="ts">
import type { AdminUnit } from 'stores/useAdminCoursesStore';

const props = defineProps<{
  unit: AdminUnit;
  index: number;
  unitNumber: number;
  isFirst: boolean;
  isLast: boolean;
  isDragging: boolean;
}>();

defineEmits<{
  (e: 'edit', unit: AdminUnit): void;
  (e: 'delete', unit: AdminUnit): void;
  (e: 'move', from: number, to: number): void;
  (e: 'drag-start', id: string): void;
  (e: 'drag-over', index: number): void;
  (e: 'drop'): void;
  (e: 'drag-end'): void;
}>();

// suppress unused warning — props is used in template
void props;
</script>

<style scoped>
.ur-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: default;
  transition: background 0.12s;
  min-height: 44px;
}

.ur-row:hover {
  background: var(--oa-hover-bg);
}

.ur-row--dragging {
  opacity: 0.6;
  box-shadow: 0 2px 8px rgba(13, 43, 69, 0.18);
  cursor: grabbing;
}

.ur-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  color: var(--oa-text-disabled);
  cursor: grab;
  border-radius: 4px;
  transition: color 0.12s;
}

.ur-handle:hover {
  color: var(--color-ocean-light, #2e7fb5);
}

.ur-row--dragging .ur-handle {
  cursor: grabbing;
}

.ur-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  background: var(--color-ocean-mid, #1a4a6b);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border-radius: 50%;
}

.ur-title {
  flex: 1;
  font-size: 15px;
  font-weight: 400;
  color: var(--oa-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}

/* Action buttons: visible on hover (desktop); always visible on mobile */
.ur-action {
  opacity: 0;
  transition: opacity 0.12s;
  color: var(--oa-text-muted);
}

.ur-row:hover .ur-action,
.ur-action--arrow {
  opacity: 1;
}

.ur-action--danger:hover {
  color: var(--color-coral, #e05a3a);
}

@media (max-width: 767px) {
  .ur-action {
    opacity: 1;
  }
}
</style>
